import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Button from '../Button/Button';
import { FailedReportDownloadModal } from '../Reports/FailedReportDownloadModal';
import { STORE_NAMES } from '../../constants/store.constants';
import { getPollReportDownloadToken, initiateExternalPollDownload, prepareExternalPollPageUrl } from '../../service/polls.service';

const DownloadPollReportButton = ({ btnClass, btnLabel = null, zoomMeetingUuid, pollReportPagePath }) => {
  // Accessing the Redux store to check if the download feature should be hidden based on the directory configuration
  const {
    attendeeReportDetails: { hideDownloadFeature }
  } = useSelector(state => state?.[STORE_NAMES.DIRECTORY_CONFIG] ?? { attendeeReportDetails: {} });

  // Local state to indicate download in-progress and avoid duplicate clicks
  const [isDownloading, setIsDownloading] = useState(false);

  // Local state to manage the display of the failed report download modal
  const [displayFailedReportDownloadModal, setDisplayFailedReportDownloadModal] = useState(false);

  const handleDownloadClick = async e => {
    // Stop event propagation to avoid triggering any parent click handlers
    e.stopPropagation();

    // prevent duplicate clicks
    setIsDownloading(true);

    // Fetch auth token for poll report download. If there's an error fetching the token, show the failure modal.
    const { success: accessToken, error } = await getPollReportDownloadToken();
    if (error) {
      setDisplayFailedReportDownloadModal(true);
      setIsDownloading(false);
      return;
    }

    // Prepare the external URL for poll report download.
    const externalUrl = prepareExternalPollPageUrl({ authToken: accessToken, zoomMeetingUuid, pollReportPagePath });

    // Initiate the external download process. If there's an error during this process, show the failure modal.
    const { error: externalDownloadProcessError } = await initiateExternalPollDownload(externalUrl);
    if (externalDownloadProcessError) {
      setDisplayFailedReportDownloadModal(true);
      setIsDownloading(false);
      return;
    }

    setIsDownloading(false);
  };

  return (
    <>
      {hideDownloadFeature === false && (
        <Button
          className={btnClass}
          onClick={handleDownloadClick}
          aria-label={btnLabel || 'Download poll report'}
          type="button"
          disabled={isDownloading}
        >
          {btnLabel}
        </Button>
      )}

      {displayFailedReportDownloadModal && <FailedReportDownloadModal onCloseHandler={() => setDisplayFailedReportDownloadModal(false)} />}
    </>
  );
};

DownloadPollReportButton.propTypes = {
  btnClass: PropTypes.string,
  btnLabel: PropTypes.string,
  zoomMeetingUuid: PropTypes.string.isRequired,
  pollReportPagePath: PropTypes.string.isRequired
};

export default DownloadPollReportButton;
